import React from 'react';
import { useCurrency } from '../../../hooks/currency';
import { ButtonIncrementDecrement } from '../../../components/button/increment-decrement/ButtonIncrementDecrement';
import { useImmer } from 'use-immer';
import { UiGrid, UiCard, UiFlex } from '@open-stack/ui';
import { XText } from '../../../components/x/text/XText';
import { XButton } from '../../../components/x/button/XButton';
import sum from 'lodash/sum';
import { SpcCheckoutHeader } from '../header/SpcCheckoutHeader';

interface Props {
    products: any;
    isLoading: boolean;

    onNext(products: any[]): any;
    onClose(): void;
}

export function SpcCheckoutChooseProducts({ products, isLoading, onNext, onClose }: Props) {
    const currency = useCurrency();

    const [productCounts, setProductCounts] = useImmer<{
        [key: string]: {
            key: string;
            productId: number;
            productName: string;
            count: number;
            price: number;
        };
    }>({});

    const selectedProducts = Object.values(productCounts).filter((item) => item.count);
    const selectedProductsTotalPrice = sum(selectedProducts.map((item) => item.count * item.price));
    const isAnyProductSelected = selectedProducts.length > 0;

    function getProductCountButton(product) {
        const { productId } = product;
        const maxCount = product.count;

        const count = productCounts[productId]?.count || 0;

        const changeCount = (direction) => (amount) => {
            setProductCounts((state) => {
                state[productId] = state[productId] || { ...product, count: 0 };
                state[productId].count += direction === 'up' ? amount : -amount;

                if (state[productId].count > maxCount) {
                    state[productId].count = maxCount;
                }
            });
        };

        return (
            <ButtonIncrementDecrement
                count={count}
                onIncrement={changeCount('up')}
                onDecrement={changeCount('down')}
                max={maxCount}
            />
        );
    }

    function next() {
        onNext(selectedProducts);
    }

    function payForAll() {
        onNext(products);
    }

    return (
        <UiGrid>
            <SpcCheckoutHeader totalCost={selectedProductsTotalPrice} onClose={onClose} />

            <UiGrid gap={1}>
                {products.map((product) => (
                    <UiCard key={product.key} padding={1} paddingLeft={2}>
                        <UiFlex justifyContent="space-between">
                            <UiGrid gap={0}>
                                <XText>
                                    {product.count} x {product.productName}
                                </XText>

                                <div>{currency.format(product.price)}</div>
                            </UiGrid>

                            {getProductCountButton(product)}
                        </UiFlex>
                    </UiCard>
                ))}
            </UiGrid>

            {isAnyProductSelected ? (
                <XButton onClick={next} isLoading={isLoading} cta color="primary">
                    Pay {currency.format(selectedProductsTotalPrice)}
                </XButton>
            ) : (
                <XButton onClick={payForAll} isLoading={isLoading} cta color="primary">
                    Pay for all
                </XButton>
            )}
        </UiGrid>
    );
}
